// 수정 동굴 분위기 — 균열 게이트(uCkCaveOn) · 굴 공기색 보라 기울이기 · 반짝 먼지 입자 한 벌.
// 켜지는 때: «카메라»가 수정 동굴 상자 안이고 지표보다 CAM_DEPTH 아래일 때. 밖이면 셋 다 0 이다(자유 시점으로 위에 떠도 꺼진다).
// 공기색은 caveLight.CAVE_AIR «객체»를 그 자리에서 바꾼다 — 굴 안개와 하늘돔 꺼진 배경이 같은 객체를 쓰므로 둘이 같이 따라온다.
// 먼지는 카메라를 따라 감싸는 상자(DUST_BOX m) 안에서 좌표를 접어 돌려 쓴다 — 드로콜 1 · 가산 · 점 DUST_N 개.
// 발광 점은 천장·벽 틈의 생물발광(크기·밝기 제각각) + 아래로 늘어진 빛실 — 자료 glows, 인스턴스 사각형 한 벌(드로콜 1).
// 빛기둥은 천장 틈에서 내려오는 차가운 달빛 — 자료 shafts 를 열린 원뿔 인스턴스 한 벌로(드로콜 1 · 가산 · 깊이 안 씀).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { CAVE_AIR, CAVE_GLOWS, CAVE_GLOW_MAX } from './caveLight';
import { hfSurfaceYAt } from './heightfieldData';
import { CAVE_CRACK_ON, CAVE_CRACK_ALLOWED } from './biome/biomeCrack';
import { CLOUD_TIME } from './sky/skyLight';
import { caveCrystal, loadCaveCrystal, inCaveCrystalBox, selectCaveAt, nearCaveMouth } from './caveCrystalZone';
import CaveWaterFx from './CaveWaterFx';
import { prewarmDetached } from '@engine/shaderWarm';

const AIR_TINTS = { geode: new THREE.Color('#1b0f33'), magma: new THREE.Color('#2a0e08'), starlight: new THREE.Color('#05070f') };
const AIR_K = 0.7;
const CAM_DEPTH = 3;
const DUST_N = 360;
const DUST_BOX = 34;
const GLOW_PICK_S = 0.4;
const GLOW_PICK_R = 70;
const GLOW_EYE = 2.5;   // m — 수면(바닥)이 카메라 아래 이만큼이라고 보고 거울상을 만든다
const _m = new THREE.Vector3();

/** 수면 반사용 발광 점 고르기 — 거울상이 화면 안에 맺히는 점 중 밝고 큰 순으로 CAVE_GLOW_MAX 개. 안이 아니면 비운다. */
function pickGlowReflections(glows, cam, inside) {
  const slots = CAVE_GLOWS.value;
  let n = 0;
  if (inside && glows.length) {
    const c = cam.position;
    const wl = c.y - GLOW_EYE;
    const r2 = GLOW_PICK_R * GLOW_PICK_R;
    const best = [];
    for (let i = 0; i < glows.length; i += 1) {
      const g = glows[i];
      const dx = g[0] - c.x, dz = g[2] - c.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > r2 || g[1] < wl + 1) continue;
      _m.set(g[0], 2 * wl - g[1], g[2]).project(cam);
      if (_m.z > 1 || Math.abs(_m.x) > 1.05 || Math.abs(_m.y) > 1.05) continue;
      best.push([g[4] * (g[3] + 0.05) / (1 + Math.sqrt(d2) * 0.03), i]);
    }
    best.sort((a, b) => b[0] - a[0]);
    for (; n < Math.min(CAVE_GLOW_MAX, best.length); n += 1) {
      const g = glows[best[n][1]];
      slots[n].set(g[0], g[1], g[2], g[6] ? -g[4] : g[4]);
    }
  }
  for (let i = n; i < CAVE_GLOW_MAX; i += 1) slots[i].w = 0;
}

const REDUCED = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const DUST_VERT = /* glsl */`
attribute float aSeed;
uniform float uTime, uBox;
uniform vec3 uCam;
varying float vA;
varying float vTeal;
void main() {
  vec3 p = position;
  p.y += mod(uTime * (0.15 + 0.25 * aSeed), uBox);
  p.x += sin(uTime * 0.3 + aSeed * 40.0) * 0.6;
  vec3 w = uCam + mod(p - uCam + uBox * 0.5, uBox) - uBox * 0.5;
  vec4 mv = viewMatrix * vec4(w, 1.0);
  float tw = 0.5 + 0.5 * sin(uTime * (2.0 + 3.0 * aSeed) + aSeed * 91.0);
  vA = tw * tw * smoothstep(uBox * 0.5, uBox * 0.2, length(w - uCam));
  vTeal = step(0.9, fract(aSeed * 7.31)) + 2.0 * step(0.8, fract(aSeed * 3.17)) * step(fract(aSeed * 7.31), 0.9);
  vA *= smoothstep(1.2, 3.5, -mv.z);
  gl_PointSize = min((2.0 + 3.5 * tw) * (60.0 / max(-mv.z, 1.0)), 14.0);
  gl_Position = projectionMatrix * mv;
}`;
const DUST_FRAG = /* glsl */`
uniform float uAmt;
varying float vA;
varying float vTeal;
void main() {
  vec2 d = gl_PointCoord - 0.5;
  float r = 1.0 - smoothstep(0.0, 0.5, length(d));
  vec3 c = vTeal > 1.5 ? vec3(1.0, 0.66, 0.3) : mix(vec3(0.78, 0.55, 1.0), vec3(0.45, 1.0, 0.9), vTeal);
  gl_FragColor = vec4(c * 1.6, r * r * vA * uAmt);
}`;

const SHAFT_VERT = /* glsl */`
varying float vH;
varying float vEdge;
varying vec3 vW;
void main() {
  vH = position.y + 0.5;
  vec4 w = modelMatrix * instanceMatrix * vec4(position, 1.0);
  vW = w.xyz;
  vec3 nW = normalize(mat3(modelMatrix * instanceMatrix) * normal);
  vEdge = abs(dot(nW, normalize(cameraPosition - w.xyz)));
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
const SHAFT_FRAG = /* glsl */`
uniform float uAmt, uTime;
varying float vH;
varying float vEdge;
varying vec3 vW;
void main() {
  float streak = 0.55 + 0.45 * sin(atan(vW.z, vW.x) * 23.0 + vW.y * 0.07 + uTime * 0.25);
  float drift = 0.75 + 0.25 * sin(vW.y * 0.35 - uTime * 0.6);
  // MSAA 는 삼각형 밖 표본점에서도 셰이더를 돌려 보간값이 0 아래로 외삽된다 — pow(음수)=NaN 이 블룸을 타고 화면 전체를 검게 만든다.
  float a = pow(max(vEdge, 0.0), 2.2) * smoothstep(0.0, 0.35, vH) * (0.35 + 0.65 * vH) * streak * drift;
  gl_FragColor = vec4(vec3(0.62, 0.72, 1.0) * a * 0.32 * uAmt, 1.0);
}`;

/** 천장 빛기둥 — shafts [[x,천장y,z,바닥y,반경]…] 를 위가 좁은 열린 원뿔 인스턴스로. */
function CaveShafts({ shafts, amt }) {
  const ref = useRef();
  const geo = useMemo(() => new THREE.CylinderGeometry(0.55, 1, 1, 20, 1, true), []);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: SHAFT_VERT, fragmentShader: SHAFT_FRAG, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, uniforms: { uAmt: { value: 0 }, uTime: { value: 0 } },
  }), []);
  useEffect(() => {
    const m = ref.current;
    if (!m) return undefined;
    const o = new THREE.Object3D();
    shafts.forEach(([x, top, z, bot, r], i) => {
      o.position.set(x, (top + bot) / 2, z);
      o.scale.set(r, top - bot, r);
      o.updateMatrix();
      m.setMatrixAt(i, o.matrix);
    });
    m.instanceMatrix.needsUpdate = true;
    return () => { geo.dispose(); mat.dispose(); };
  }, [shafts, geo, mat]);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    m.visible = amt.current > 0.01;
    const u = m.material.uniforms;
    u.uAmt.value = amt.current;
    u.uTime.value = REDUCED ? 0 : CLOUD_TIME.value;
  });
  return <instancedMesh ref={ref} args={[geo, mat, shafts.length]} frustumCulled={false} renderOrder={4} name="cave-crystal-shafts" />;
}

const GLOW_VERT = /* glsl */`
attribute vec3 iP;
attribute vec4 iS;
uniform float uTime;
varying vec2 vL;
varying vec4 vS;
varying float vTw;
void main() {
  vec3 f = cameraPosition - iP;
  float dist = length(f);
  // 점마다 고정 난수 — 밝기는 어두운 쪽으로 치우치게(대부분 은은 · 드물게 밝게), 크기·깜빡임 폭도 제각각.
  float sd = fract(iP.x * 0.731 + iP.z * 0.377 + iP.y * 0.113);
  float h2 = fract(sin(dot(iP, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
  float h3 = fract(h2 * 17.13 + sd * 5.7);
  float sz = iS.x * (0.6 + 0.6 * h3);
  float br = iS.y * (0.2 + 0.8 * h2 * h2 * h2);
  float grow = max(1.0, dist * 0.0065 / sz);
  float halo = sz * 3.0 * grow;
  float H = halo + iS.z;
  vec3 fw = normalize(vec3(f.x, 0.0, f.z) + vec3(1e-4, 0.0, 0.0));
  vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), fw));
  float dx = position.x * halo;
  float dy = halo * 0.5 - (0.5 - position.y) * H;
  vL = vec2(dx, dy);
  vS = vec4(sz * grow, br * (0.6 + 0.4 / grow) * min(grow, 1.6), iS.z, iS.w);
  float tw = 0.15 + 0.35 * h3;
  vTw = 1.0 - tw + tw * sin(uTime * (0.4 + sd) + sd * 40.0);
  vec4 mv = viewMatrix * vec4(iP + right * dx + vec3(0.0, dy, 0.0), 1.0);
  vTw *= smoothstep(1.5, 4.0, -mv.z);
  gl_Position = projectionMatrix * mv;
}`;
const GLOW_FRAG = /* glsl */`
uniform float uAmt;
varying vec2 vL;
varying vec4 vS;
varying float vTw;
void main() {
  float s = vS.x;
  float r2 = dot(vL, vL);
  float dot0 = exp(-r2 / (s * s * 0.18)) * 2.4 + exp(-sqrt(r2) / (s * 0.9)) * 0.5;
  float wd = max(0.012, fwidth(vL.x));
  float t = clamp(-vL.y / max(vS.z, 1e-3), 0.0, 1.0);
  float bead = 0.55 + 0.45 * smoothstep(0.6, 1.0, sin(t * 38.0 + vS.x * 90.0));
  float thread = step(vL.y, 0.0) * (1.0 - smoothstep(0.0, wd, abs(vL.x))) * (0.012 / wd) * pow(1.0 - t, 1.6) * bead * 0.9;
  vec3 c = vS.w > 0.5 ? vec3(0.72, 0.58, 1.0) : vec3(0.32, 0.82, 1.0);
  gl_FragColor = vec4(c * (dot0 + thread) * vS.y * vTw * uAmt, 1.0);
}`;

/** 천장·벽 틈 발광 점 — glows [[x,y,z,크기,밝기,실 길이,색]…]. 세로축만 카메라를 보는 사각형 한 벌(드로콜 1 · 가산 · 깊이 안 씀). */
function CaveGlowworms({ glows, amt }) {
  const ref = useRef();
  const geo = useMemo(() => {
    const g = new THREE.InstancedBufferGeometry();
    const base = new THREE.PlaneGeometry(1, 1);
    g.index = base.index;
    g.setAttribute('position', base.attributes.position);
    const p = new Float32Array(glows.length * 3);
    const s = new Float32Array(glows.length * 4);
    glows.forEach(([x, y, z, sz, br, th, c], i) => {
      p.set([x, y, z], i * 3);
      s.set([sz, br, th, c], i * 4);
    });
    g.setAttribute('iP', new THREE.InstancedBufferAttribute(p, 3));
    g.setAttribute('iS', new THREE.InstancedBufferAttribute(s, 4));
    g.instanceCount = glows.length;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e7);
    return g;
  }, [glows]);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: GLOW_VERT, fragmentShader: GLOW_FRAG, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, uniforms: { uAmt: { value: 0 }, uTime: { value: 0 } },
  }), []);
  useEffect(() => () => { geo.dispose(); mat.dispose(); }, [geo, mat]);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    m.visible = amt.current > 0.01;
    const u = m.material.uniforms;
    u.uAmt.value = amt.current;
    u.uTime.value = REDUCED ? 0 : CLOUD_TIME.value;
  });
  return <mesh ref={ref} geometry={geo} material={mat} frustumCulled={false} renderOrder={4} name="cave-crystal-glowworms" />;
}

export default function CaveCrystalFx() {
  const airBase = useMemo(() => CAVE_AIR.clone(), []);
  const amt = useRef(0);
  const dustRef = useRef();
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(DUST_N * 3);
    const seed = new Float32Array(DUST_N);
    let h = 12345;
    const rnd = () => { h = (Math.imul(h, 1664525) + 1013904223) >>> 0; return h / 4294967296; };
    for (let i = 0; i < DUST_N; i += 1) {
      pos[i * 3] = rnd() * DUST_BOX; pos[i * 3 + 1] = rnd() * DUST_BOX; pos[i * 3 + 2] = rnd() * DUST_BOX;
      seed[i] = rnd();
    }
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e7);
    return g;
  }, []);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: DUST_VERT, fragmentShader: DUST_FRAG, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { uTime: { value: 0 }, uBox: { value: DUST_BOX }, uCam: { value: new THREE.Vector3() }, uAmt: { value: 0 } },
  }), []);

  const [shafts, setShafts] = useState([]);
  const [glows, setGlows] = useState([]);
  const [water, setWater] = useState({ falls: [], lake: null });
  const pickT = useRef(0);
  const rev = useRef(-1);
  const sync = (c) => { rev.current = c.rev; setShafts(c.shafts || []); setGlows(c.glows || []); setWater({ falls: c.falls || [], lake: c.lake }); };
  useEffect(() => { loadCaveCrystal().then(sync); }, []);
  useEffect(() => () => {
    CAVE_CRACK_ON.value = 0;
    CAVE_AIR.copy(airBase);
    geo.dispose();
    mat.dispose();
  }, [airBase, geo, mat]);

  useFrame(({ camera }, dt) => {
    const c = camera.position;
    selectCaveAt(c.x, c.z);
    if (caveCrystal.ready && caveCrystal.rev !== rev.current) sync(caveCrystal);
    const surf = inCaveCrystalBox(c.x, c.z) ? hfSurfaceYAt(c.x, c.z) : null;
    const inside = surf != null && c.y < surf - CAM_DEPTH;
    const mouth = !inside && surf != null && nearCaveMouth(c.x, c.z, caveCrystal.fade * 2.5);   // 밖에서 갱구 안을 미리 본다
    CAVE_CRACK_ON.value = CAVE_CRACK_ALLOWED && (inside || mouth) ? 1 : 0;
    amt.current += ((inside ? 1 : 0) - amt.current) * Math.min(1, dt * 1.5);
    if (amt.current < 0.002) amt.current = 0;
    CAVE_AIR.copy(airBase).lerp(AIR_TINTS[caveCrystal.theme] || AIR_TINTS.geode, amt.current * AIR_K);
    pickT.current -= dt;
    if (pickT.current <= 0) { pickT.current = GLOW_PICK_S; pickGlowReflections(glows, camera, inside); }
    const d = dustRef.current;
    if (d) {
      d.visible = amt.current > 0.01;
      const u = d.material.uniforms;
      u.uAmt.value = amt.current;
      u.uCam.value.copy(c);
      u.uTime.value = REDUCED ? 0 : CLOUD_TIME.value;
    }
  });

  // 굴에 들어서는 순간 여섯 셰이더가 한 프레임에 컴파일되지 않게, 자료가 바뀔 때마다 숨긴 채 미리 컴파일한다.
  const rootRef = useRef();
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    try { prewarmDetached(root)?.catch(() => {}); } catch { /* 첫 진입 때 굽는다 */ }
  }, [shafts, glows, water]);

  return (
    <group ref={rootRef} name="cave-crystal-fx">
      <points ref={dustRef} geometry={geo} material={mat} frustumCulled={false} visible={false} renderOrder={5} name="cave-crystal-dust" />
      {shafts.length > 0 && <CaveShafts shafts={shafts} amt={amt} />}
      {glows.length > 0 && <CaveGlowworms glows={glows} amt={amt} />}
      <CaveWaterFx falls={water.falls} lake={water.lake} amt={amt} />
    </group>
  );
}
